/**
 * Authentication error codes matching backend
 */
export const AuthErrorCode = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT',
  PASSWORD_RESET_TOKEN_INVALID: 'PASSWORD_RESET_TOKEN_INVALID',
  PASSWORD_RESET_TOKEN_EXPIRED: 'PASSWORD_RESET_TOKEN_EXPIRED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
} as const;

export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

export interface AuthErrorResponse {
  success: false;
  statusCode: number;
  message: string;
  error: AuthErrorCode | string;
  timestamp: string;
  path: string;
  method?: string;
  details?: Record<string, any>;
}

/**
 * User-friendly error messages for authentication errors
 */
export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  [AuthErrorCode.INVALID_CREDENTIALS]:
    'Invalid email or password. Please check your credentials and try again.',
  [AuthErrorCode.USER_NOT_FOUND]: 'No account found with this email address.',
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]:
    'Error performing registration. Potentially there is already an account associated with the given E-Mail.',
  [AuthErrorCode.INVALID_TOKEN]:
    'Your session is invalid. Please log in again.',
  [AuthErrorCode.TOKEN_EXPIRED]:
    'Your session has expired. Please log in again.',
  [AuthErrorCode.ACCOUNT_LOCKED]:
    'Your account has been temporarily locked due to multiple failed login attempts. Please try again later.',
  [AuthErrorCode.WEAK_PASSWORD]:
    'Password does not meet security requirements. Please choose a stronger password.',
  [AuthErrorCode.INVALID_EMAIL_FORMAT]: 'Please enter a valid email address.',
  [AuthErrorCode.PASSWORD_RESET_TOKEN_INVALID]:
    'This password reset link is invalid. Please request a new one.',
  [AuthErrorCode.PASSWORD_RESET_TOKEN_EXPIRED]:
    'This password reset link has expired. Please request a new one.',
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]:
    'Too many requests. Please wait a moment before trying again.',
  [AuthErrorCode.ACCOUNT_INACTIVE]:
    'Your account is inactive. Please contact support for assistance.',
  [AuthErrorCode.UNAUTHORIZED]:
    'You need to be logged in to access this feature.',
  [AuthErrorCode.FORBIDDEN]:
    "You don't have permission to access this feature.",
};

/**
 * Get user-friendly error message from error response
 */
export function getErrorMessage(error: any): string {
  // Handle network errors (no response means network/connection issue)
  if (!error.response) {
    return 'Network error. Please check your connection and try again.';
  }

  const errorResponse = error.response.data as AuthErrorResponse;

  // Handle authentication errors with specific codes
  if (
    errorResponse?.error &&
    Object.values(AuthErrorCode).includes(errorResponse.error as AuthErrorCode)
  ) {
    return AUTH_ERROR_MESSAGES[errorResponse.error as AuthErrorCode];
  }

  // Handle conflict errors (409) - like email already exists
  if (errorResponse?.statusCode === 409) {
    // Check for specific email already exists message
    if (
      errorResponse?.message?.toLowerCase().includes('email already exists')
    ) {
      return AUTH_ERROR_MESSAGES[AuthErrorCode.EMAIL_ALREADY_EXISTS];
    }
    return (
      errorResponse?.message ||
      'A conflict occurred. Please check your input and try again.'
    );
  }

  // Handle validation errors (400)
  if (errorResponse?.statusCode === 400 && errorResponse?.message) {
    // Handle array of validation messages
    if (Array.isArray(errorResponse.message)) {
      return formatValidationErrors(errorResponse.message);
    }
    return errorResponse.message;
  }

  // Handle server errors
  if (errorResponse?.statusCode >= 500) {
    return 'Server error. Please try again later.';
  }

  // Fallback to generic message
  return (
    errorResponse?.message || 'An unexpected error occurred. Please try again.'
  );
}

/**
 * Check if error is an authentication error that requires re-login
 */
export function isAuthenticationError(error: any): boolean {
  const errorResponse = error.response?.data as AuthErrorResponse;

  if (!errorResponse) return false;

  const authErrors = [
    AuthErrorCode.INVALID_TOKEN,
    AuthErrorCode.TOKEN_EXPIRED,
    AuthErrorCode.UNAUTHORIZED,
  ];

  return (
    authErrors.some((code) => code === errorResponse.error) ||
    errorResponse.statusCode === 401
  );
}

/**
 * Check if error is a rate limiting error
 */
export function isRateLimitError(error: any): boolean {
  const errorResponse = error.response?.data as AuthErrorResponse;

  return (
    errorResponse?.error === AuthErrorCode.RATE_LIMIT_EXCEEDED ||
    errorResponse?.statusCode === 429
  );
}

/**
 * Get retry delay from rate limit error
 */
export function getRetryDelay(error: any): number | null {
  const errorResponse = error.response?.data as AuthErrorResponse;

  if (isRateLimitError(error)) {
    return errorResponse?.details?.retryAfter || 60; // Default to 60 seconds
  }

  return null;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: string[]): string {
  if (errors.length === 1) {
    return errors[0];
  }

  return `Please fix the following issues:\n• ${errors.join('\n• ')}`;
}

/**
 * Enhanced error class for authentication errors
 */
export class AuthError extends Error {
  public code: string;
  public statusCode: number;
  public details?: Record<string, any>;
  public originalError?: any;

  constructor(
    message: string,
    code: string,
    statusCode: number,
    details?: Record<string, any>,
    originalError?: any,
  ) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.originalError = originalError;
  }
}

/**
 * Create enhanced error from axios error
 */
export function createEnhancedError(error: any): AuthError {
  // Handle network errors
  if (!error.response) {
    // Log more details about network errors for debugging
    console.error('Network error details:', {
      message: error.message,
      code: error.code,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
      },
      request: error.request,
    });

    // Provide more specific error messages based on error code
    let errorMessage =
      'Network error. Please check your connection and try again.';
    if (error.code === 'ECONNREFUSED') {
      errorMessage =
        'Cannot connect to server. Please ensure the backend is running.';
    } else if (error.code === 'ERR_NETWORK') {
      errorMessage = 'Network request failed. Please check your connection.';
    } else if (
      error.code === 'ETIMEDOUT' ||
      error.message?.includes('timeout')
    ) {
      errorMessage = 'Request timed out. Please try again.';
    }

    return new AuthError(
      errorMessage,
      'NETWORK_ERROR',
      0,
      { code: error.code, message: error.message },
      error,
    );
  }

  const errorResponse = error.response.data as AuthErrorResponse;
  const statusCode = error.response.status;

  // Use the error code from the response or map status code to error code
  let errorCode = errorResponse?.error || 'UNKNOWN_ERROR';
  let message = errorResponse?.message || error.message;

  // Map specific status codes to error codes and messages
  if (statusCode === 401) {
    if (errorCode === 'INVALID_CREDENTIALS') {
      message = 'Invalid email or password';
    } else if (errorCode === 'INVALID_TOKEN') {
      message = 'Invalid refresh token';
    }
  } else if (
    statusCode === 400 &&
    errorResponse?.message?.toLowerCase().includes('password')
  ) {
    message = 'Current password is incorrect';
    errorCode = 'INVALID_CREDENTIALS';
  }

  return new AuthError(
    message,
    errorCode,
    statusCode,
    errorResponse?.details,
    error,
  );
}
